import { isAndroid, isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { stopVegaHtmlAudioTap } from "src/lipsync/vegaLipSync";

/** Mobile web + Capacitor Android: delayed play() of remote URLs needs a gesture-unlocked persistent element. iOS keeps the passed element (HTMLAudio tap). */
function usePersistentHtmlAudio(): boolean {
  return isMobileWebBrowser() || (mobile && isAndroid);
}

/**
 * A type for the listener function that components will use to subscribe to changes.
 * It receives the new `isMuted` state as a boolean.
 */
type Listener = (isMuted: boolean) => void;
type VolumeListener = (volume: number) => void;

export enum AudioPriority {
  LOW = 1, // Welcome audio
  MEDIUM = 2, // Funny audio
  HIGH = 3, // Rundown, Waiver, Start, Trade
}

interface NowPlaying {
  audio: HTMLAudioElement;
  priority: AudioPriority;
}

/** Optional pause/resume hooks for audio paths not fully owned by nowPlaying (e.g. PCM players, extra HTML elements). */
export type BackgroundOverlayAudioBridge = {
  pause: () => void;
  resume: () => void;
};

/**
 * A singleton service to manage the global mute state for all audio in the application.
 * This uses a simple Publish-Subscribe (Pub/Sub) pattern.
 * - `toggleMute` is the "publisher" that broadcasts a change.
 * - `subscribe` allows components to become "subscribers" to those changes.
 * This avoids prop-drilling the mute state through many components.
 */
class AudioService {
  // Private state for the mute status.
  private isMuted = false;
  private volume = 1; // Default volume is 100%
  // A Set to hold all the subscribed listener functions. A Set is used to prevent duplicates.
  private listeners = new Set<Listener>();
  private volumeListeners = new Set<VolumeListener>();
  private nowPlaying: NowPlaying | null = null;
  // Invalidate in-flight play() so pause() is not overwritten after drawer close.
  private playbackGeneration = 0;
  // Initialize as null. It will only be created on mobile web browsers.
  // Capacitor Android also creates it (delayed GCS play); Capacitor iOS does not.
  private summaryPlayer: HTMLAudioElement | null = null;
  private hasUnlockedAudio = false;
  private backgroundOverlayBridges = new Set<BackgroundOverlayAudioBridge>();

  constructor() {
    if (typeof window !== "undefined") {
      // --- MODIFICATION ---
      // Only create the dedicated player if we are on a mobile web browser.
      // This prevents creating unnecessary audio elements on Capacitor/Desktop.
      // Capacitor Android also needs it: WebView blocks delayed play() on a fresh element (iOS does not).
      if (usePersistentHtmlAudio()) {
        this.summaryPlayer = new Audio();
        // Pre-load it with a tiny, silent WAV file to avoid network requests.
        this.summaryPlayer.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

        // When this persistent player finishes playing a real audio, clear its state.
        this.summaryPlayer.onended = () => {
          if (this.nowPlaying && this.nowPlaying.audio === this.summaryPlayer) {
            this.nowPlaying = null;
          }
        };
      }
    }
  }

  /**
   * "Unlocks" the dedicated summaryPlayer by playing it silently on the first user tap.
   * This gives it permission for future programmatic playback on iOS.
   */
  unlockAudio = (): void => {
    if (this.summaryPlayer && !this.hasUnlockedAudio) {
      this.hasUnlockedAudio = true; // We only need to do this once.
      this.summaryPlayer.volume = 0; // Ensure it's silent.
      this.summaryPlayer.play().catch(() => {
        // This error is expected on some browsers, but the user interaction
        // has successfully "blessed" the element for future plays.
        console.warn("Audio unlock play() attempt failed (this is expected).");
      });
    }
  };

  /**
   * A simple getter to allow components to synchronously get the current volume on mount.
   */
  getVolume = (): number => {
    return this.volume;
  };

  /**
   * Sets the volume for all audio playback and notifies listeners.
   * @param newVolume The new volume level, from 0.0 to 1.0.
   */
  setVolume = (newVolume: number): void => {
    this.volume = Math.max(0, Math.min(1, newVolume)); // Clamp between 0 and 1

    if (this.nowPlaying) {
      this.nowPlaying.audio.volume = this.volume;
    }

    // Also update the dedicated summary player if it exists
    if (this.summaryPlayer) {
      this.summaryPlayer.volume = this.volume;
    }

    // Notify all volume listeners of the change
    this.volumeListeners.forEach((listener) => listener(this.volume));
  };

  /**
   * Allows a component to subscribe to volume state changes.
   * @param listener The callback function to execute when the volume state changes.
   * @returns An `unsubscribe` function to clean up the listener when the component unmounts.
   */
  subscribeToVolume(listener: VolumeListener): () => void {
    this.volumeListeners.add(listener);
    // Immediately notify the new subscriber with the current volume.
    listener(this.volume);
    // Return a cleanup function.
    return () => {
      this.volumeListeners.delete(listener);
    };
  }

  /**
   * Allows a component to subscribe to mute state changes.
   * @param listener The callback function to execute when the mute state changes.
   * @returns An `unsubscribe` function to clean up the listener when the component unmounts.
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Immediately notify the new subscriber with the current state.
    listener(this.isMuted);
    // Return a cleanup function.
    return () => {
      this.listeners.delete(listener);
    };
  }

  //play the audio function with priority
  playAudio = (audio: HTMLAudioElement, priority: AudioPriority): boolean => {
    const persistent = usePersistentHtmlAudio();

    //only for mobile web (safari/google inside mobiles), otherwise it not used this logic
    // Capacitor Android: same persistent player so team-name/agent/trade play() works after Vega wait.
    if (persistent) {
      //if there is already an audio playing, check if the new audio has a higher priority
      if (this.nowPlaying) {
        //if there is already an audio playing, check if the new audio has a higher priority
        if (priority >= this.nowPlaying.priority) {
          //if the new audio has a higher priority, stop the current audio
          this.stopCurrentAudio();
        } else {
          // A higher or equal priority audio is already playing.
          return false;
        }
      }

      //create a new audio element to use
      let playerToUse: HTMLAudioElement;

      // --- Core of the iOS Fix, now conditionally applied ---
      // This condition now works perfectly. `this.summaryPlayer` will only exist on mobile web,
      // ensuring this special logic path is exclusive to that environment.
      // MEDIUM+ (funny team audio + endpoint summaries) use summaryPlayer so delayed play() works after user gesture.
      // Capacitor Android uses the same transfer; Capacitor iOS still plays the passed element.
      if (priority >= AudioPriority.MEDIUM && this.summaryPlayer) {
        this.summaryPlayer.src = audio.src; // Transfer the real URL to our player
        playerToUse = this.summaryPlayer;
      } else {
        // Welcome audio (LOW) and desktop/native: use the temporary element passed in.
        playerToUse = audio;
      }
      // --- End of Fix ---

      this.nowPlaying = { audio: playerToUse, priority };
      playerToUse.muted = this.isMuted;
      playerToUse.volume = this.volume; // Set volume from service state

      const gen = this.playbackGeneration;
      playerToUse.play().then(() => {
        if (gen !== this.playbackGeneration) {
          playerToUse.volume = 0;
          playerToUse.pause();
        }
      }).catch((err) => {
        console.error("Audio play failed:", err);
        if (this.nowPlaying && this.nowPlaying.audio === playerToUse) {
          this.nowPlaying = null; // Clear if play fails
        }
      });

      // For temporary players, we need to assign the onended handler here.
      // The summaryPlayer's handler is permanent and was set in the constructor.
      if (playerToUse !== this.summaryPlayer) {
        playerToUse.onended = () => {
          if (this.nowPlaying && this.nowPlaying.audio === playerToUse) {
            this.nowPlaying = null;
          }
        };
      }

      //return true to indicate that the audio was played
      return true;
      //if it is not mobile web, use the original logic for audios
    } else {
      //if there is already an audio playing, check if the new audio has a higher priority
      if (this.nowPlaying) {
        //if there is already an audio playing, check if the new audio has a higher priority
        if (priority >= this.nowPlaying.priority) {
          //if the new audio has a higher priority, stop the current audio
          this.stopCurrentAudio();
        } else {
          // A higher or equal priority audio is already playing.
          return false;
        }
      }

      //if there is no audio playing, play the new audio
      this.nowPlaying = { audio, priority };
      //set the audio to the current mute state
      audio.muted = this.isMuted;
      audio.volume = this.volume; // Set volume from service state
      const gen = this.playbackGeneration;
      //play the audio
      audio.play().then(() => {
        if (gen !== this.playbackGeneration) {
          audio.volume = 0;
          audio.pause();
        }
      }).catch((err) => {
        console.error("Audio play failed:", err);
        this.nowPlaying = null; // Clear if play fails
      });

      // When the audio finishes, clear it from nowPlaying
      audio.onended = () => {
        this.nowPlaying = null;
      };

      //return true to indicate that the audio was played
      return true;
    }
  };

  //stop the current audio
  stopCurrentAudio = (): void => {
    this.playbackGeneration += 1;
    const el = this.nowPlaying?.audio ?? null;
    if (el) {
      // Kill the iOS Vega tap before pause so ScriptProcessor cannot repeat the last buffer.
      stopVegaHtmlAudioTap(el);
      el.volume = 0;
      el.pause(); //pause the audio
      // this.nowPlaying.audio.currentTime = 0; //reset the audio to the beginning
    }
    // Mobile web / Android persistent player can keep speaking after nowPlaying is cleared.
    if (this.summaryPlayer && this.summaryPlayer !== el) {
      this.summaryPlayer.volume = 0;
      this.summaryPlayer.pause();
    }
    this.nowPlaying = null; //clear the current audio
  };

  // Pause without clearing nowPlaying so it can be resumed later
  pauseCurrentAudio = (): void => {
    if (this.nowPlaying && !this.nowPlaying.audio.paused) {
      this.nowPlaying.audio.pause();
    }
  };

  // Resume audio that was paused (not stopped).
  // On iOS (Capacitor & Safari), play() may fail after returning from background because the
  // audio session is still being restored. We retry once with a short delay if the first attempt fails.
  resumeCurrentAudio = (): void => {
    if (!this.nowPlaying || !this.nowPlaying.audio.paused) return;
    const audio = this.nowPlaying.audio;

    audio.play().catch((err) => {
      console.warn("[audioService] resumeCurrentAudio play() failed, retrying in 350ms:", err);
      setTimeout(() => {
        if (this.nowPlaying && this.nowPlaying.audio === audio && audio.paused) {
          audio.play().catch((err2) => {
            console.error("[audioService] resumeCurrentAudio retry also failed:", err2);
          });
        }
      }, 350);
    });
  };

  /**
   * Register extra pause/resume for welcome/funny/trade PCM etc. Unregister via returned cleanup.
   */
  registerBackgroundOverlayBridge = (bridge: BackgroundOverlayAudioBridge): (() => void) => {
    this.backgroundOverlayBridges.add(bridge);
    return () => {
      this.backgroundOverlayBridges.delete(bridge);
    };
  };

  /** Pause managed HTML audio + all registered bridges (full-screen background overlay). */
  pauseForBackgroundOverlay = (): void => {
    this.pauseCurrentAudio();
    this.backgroundOverlayBridges.forEach((b) => {
      try {
        b.pause();
      } catch {
        /* noop */
      }
    });
  };

  /** Resume after overlay hides. */
  resumeAfterBackgroundOverlay = (): void => {
    this.resumeCurrentAudio();
    this.backgroundOverlayBridges.forEach((b) => {
      try {
        b.resume();
      } catch {
        /* noop */
      }
    });
  };

  /**
   * Toggles the mute state and notifies all subscribed listeners of the change.
   * This is called by the main mute button component.
   */
  toggleMute = (): void => {
    this.setMuted(!this.isMuted);
  };

  /**
   * Sets the mute state and notifies listeners only when changed.
   */
  setMuted = (muted: boolean): void => {
    if (this.isMuted === muted) return;
    this.isMuted = muted;
    this.listeners.forEach((listener) => listener(this.isMuted));

    //if there is an audio playing, set the mute state to the current mute state
    if (this.nowPlaying) {
      this.nowPlaying.audio.muted = this.isMuted;
    }
  };

  /**
   * Returns the actual HTMLAudioElement currently being used for playback,
   * or null if nothing is playing.
   *
   * NOTE: On mobile web, HIGH priority audio may be routed through the dedicated
   * `summaryPlayer`, so this may not be the same element passed into `playAudio()`.
   */
  getNowPlayingAudio = (): HTMLAudioElement | null => {
    return this.nowPlaying?.audio ?? null;
  };

  /**
   * Returns the priority of the currently playing audio.
   */
  getNowPlayingPriority = (): AudioPriority | null => {
    return this.nowPlaying?.priority ?? null;
  };

  /**
   * A simple getter to allow components to synchronously get the current mute state on mount.
   */
  getState = (): { isMuted: boolean } => {
    return { isMuted: this.isMuted };
  };
}

// We export a single, shared instance of the service (a singleton).
// This ensures that all parts of the app are communicating with the same "radio station".
export const audioService = new AudioService();
